import type { IndividualPaymentFilters } from '../types/individualPayment';

/** Campos de filtro derivados da seleção no lookup de aluno. */
export type StudentFilterFields = Pick<
  IndividualPaymentFilters,
  'personId' | 'studentLabel' | 'studentName' | 'studentEmail'
>;

/** Conteúdo entre colchetes do rótulo — onde vem o e-mail. */
const BRACKET_CONTENT = /\[([^\]]*)\]/;
/** Prefixo textual da chave do lookup (ex.: `USR` em `"USR260953"`). */
const LEADING_NON_DIGITS = /^\D+/;
const DIGITS_ONLY = /^\d+$/;

/**
 * Extrai o id numérico da chave do lookup de pessoa, removendo o prefixo
 * textual.
 *
 * `person/shared/people-multiple` devolve a chave como `"USR260953"`. É esse
 * prefixo que impedia o uso em `PersonIds`: `Number('USR260953')` é `NaN`, e o
 * filtro de aluno era descartado em silêncio na montagem da query — a pesquisa
 * saía sem nenhum filtro mesmo com um aluno selecionado na tela.
 *
 * @param key - Chave crua do lookup.
 * @returns Só os dígitos, ou `''` se o restante não for um número.
 *
 * @example
 * ```ts
 * parseStudentKeyId('USR260953'); // '260953'
 * parseStudentKeyId('USR-A1');    // '' (não é id)
 * ```
 */
export function parseStudentKeyId(key: string): string {
  const digits = key.trim().replace(LEADING_NON_DIGITS, '');
  return DIGITS_ONLY.test(digits) ? digits : '';
}

/** Partes pesquisáveis do rótulo do lookup de aluno. */
export interface StudentLabelParts {
  /** Nome do aluno (trecho antes dos colchetes), com espaços normalizados. */
  readonly name: string;
  /** E-mail entre colchetes; `''` quando o rótulo não traz um. */
  readonly email: string;
}

/**
 * Quebra o rótulo do lookup de aluno em nome e e-mail.
 *
 * O rótulo vem no formato `"Lucas  [lucas@exemplo.com]"`. O conteúdo dos
 * colchetes só é tratado como e-mail se contiver `@`; qualquer outra coisa é
 * ignorada em vez de virar um filtro inválido.
 *
 * @param label - Rótulo exibido pelo lookup.
 *
 * @example
 * ```ts
 * parseStudentLabel('Lucas  [lucas@exemplo.com]');
 * // { name: 'Lucas', email: 'lucas@exemplo.com' }
 * ```
 */
export function parseStudentLabel(label: string): StudentLabelParts {
  const match = BRACKET_CONTENT.exec(label);
  const bracket = match?.[1]?.trim() ?? '';
  const email = bracket.includes('@') ? bracket : '';
  const name = (match ? label.slice(0, match.index) : label).replace(/\s+/g, ' ').trim();

  return { name, email };
}

/**
 * Converte a seleção do autocomplete de aluno nos campos de filtro
 * correspondentes: id da chave, rótulo para exibição, nome e e-mail do rótulo.
 * Seleção vazia (limpeza do campo) zera os quatro.
 *
 * @param key - Chave do item selecionado (`"USR260953"`).
 * @param label - Rótulo do item selecionado (`"Lucas  [lucas@exemplo.com]"`).
 *
 * @example
 * ```ts
 * studentFiltersFromSelection('USR260953', 'Lucas  [lucas@exemplo.com]');
 * // { personId: '260953', studentLabel: 'Lucas  [lucas@exemplo.com]',
 * //   studentName: 'Lucas', studentEmail: 'lucas@exemplo.com' }
 * ```
 */
export function studentFiltersFromSelection(key: string, label: string): StudentFilterFields {
  const { name, email } = parseStudentLabel(label);

  return {
    personId: parseStudentKeyId(key),
    studentLabel: label,
    studentName: name,
    studentEmail: email,
  };
}
